import { StrictMode, useRef } from "react";
import { Box, Skeleton } from "@mui/material";
import dynamic from "next/dynamic";
import { useTranslation } from "next-i18next";
import { Typography } from "src/components-mui/base/typography/typography";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { useIsSectionInView } from "src/components-mui/common/jump-marks/use-is-section-in-view";
import { ITreemapChartProps } from "src/components-mui/pages/detail/treemap-chart/treemap-chart";
import { TreemapChartSkeleton } from "src/components-mui/pages/detail/treemap-chart/treemap-chart-skeleton";
import { ITreemapChart } from "src/components-mui/pages/detail/treemap-chart/types";
import { EnumWikifolioStatus } from "src/types/common-enum";

export const TREEMAP_CHART_TEST_ID = "tree-map-chart";

const TreemapChartYearsRangeSkeleton = () => (
    <Skeleton variant="rounded" sx={{ borderRadius: "8px", mb: 2, height: 48, width: { xs: "100%", sm: 294 }, ml: "auto" }} />
);

const TreemapChart = dynamic<ITreemapChartProps>(
    () => import("src/components-mui/pages/detail/treemap-chart/treemap-chart").then(component => component.TreemapChart),
    {
        loading: () => (
            <>
                <TreemapChartYearsRangeSkeleton />
                <TreemapChartSkeleton />
            </>
        ),
        ssr: false,
    }
);

export const TreemapChartSection = ({ dict, symbol, creationDate, wikifolioStatus }: ITreemapChart) => {
    const { t } = useTranslation("wf-detail");
    const { sectionRef, isVisible } = useTreemapChartVisibility();

    const shouldRenderPnlChart = [
        EnumWikifolioStatus.Investable,
        EnumWikifolioStatus.Published,
        EnumWikifolioStatus.IssuingRequested,
        EnumWikifolioStatus.EmissionProcessStarted,
        EnumWikifolioStatus.ReadyForIssuing,
    ].includes(wikifolioStatus);

    return shouldRenderPnlChart ? (
        <StrictMode>
            <Box component="section" ref={sectionRef} data-test-id={TREEMAP_CHART_TEST_ID}>
                <WfHeading component="h2" fontSize="2xl" sx={{ mb: 2 }}>
                    {dict.title}
                </WfHeading>
                <Typography component="p" sx={{ mb: 4 }}>
                    {dict.description}
                </Typography>

                {isVisible ? (
                    <TreemapChart symbol={symbol} creationDate={creationDate} chartAriaLabel={t("labels.treemap-chart-aria-label")} />
                ) : (
                    <>
                        <TreemapChartYearsRangeSkeleton />
                        <TreemapChartSkeleton />
                    </>
                )}
            </Box>
        </StrictMode>
    ) : null;
};

function useTreemapChartVisibility() {
    const sectionRef = useRef<HTMLDivElement>(null);

    const isVisible = useIsSectionInView({ sectionRef, threshold: 0.5, visibleOnce: true });

    return { sectionRef, isVisible };
}
